/*
 * "What have I switched off and forgotten?" — the third block of the notification settings screen,
 * and the one piece of it core cannot answer on its own.
 *
 * Per-conversation notification state (muted until, and the level "all / mention / none") is
 * membership state, which belongs to the module that owns conversations: it lives in chat's own
 * slice of the model and chat's route changes it. Core may not read inside a module's slice or name
 * a module's URL (architecture.md D21, and `eslint.config.mjs` enforces the import half), so the
 * module hands core two functions instead — read the rows, put one back — and core does the rest:
 * the address of a conversation, where it lives, the table, the copy.
 *
 * One binding, offered by the module as `provides.conversationNotifications` and registered by the
 * boot (`core/modules/bind.ts`). Nothing registered means no module in this build has
 * per-conversation notifications, and the block says so rather than pretending the list is empty.
 */

import { useMemo } from "react"
import { conversationHref } from "../../routes/links.ts"
import { conversationAddress } from "../store/conversationName.ts"
import type { ConversationId, Model } from "../store/model.ts"
import { useModel } from "../store/store.ts"

/** What a conversation notifies its member about; the server's three values. */
export type NotifyLevel = "all" | "mention" | "none"

/** One conversation whose notifications this person has moved away from the default. */
export interface ConversationOverride {
	conv: ConversationId
	level: NotifyLevel
	/**
	 * Whether `level` is a choice this person made rather than the level the conversation handed them.
	 * Which level that is belongs to the module that owns conversations — a channel is given *Only
	 * mentions*, a direct conversation *All new messages* — so the binding says it and core only reads
	 * it: a row that is merely muted offers no level to put back, and a channel somebody turned up to
	 * *All new messages* does offer one.
	 */
	levelChanged: boolean
	/** Set while the conversation is muted; a timestamp in the future, or far away for "forever". */
	mutedUntil?: number | undefined
}

/** Which half of an override a row is putting back. */
export type OverrideReset = "mute" | "level"

export interface ConversationNotifications {
	/** Every conversation this person has muted or set to something other than every message. */
	overrides(model: Model): ConversationOverride[]
	/** Unmutes a conversation ("mute") or sets it back to every message ("level"). */
	reset(conv: ConversationId, what: OverrideReset): Promise<void>
}

let binding: ConversationNotifications | undefined

/** Called once at boot with the offer of the module that owns conversations. */
export function registerConversationNotifications(source: ConversationNotifications): void {
	binding = source
}

/** The binding, or undefined in a build whose modules have no per-conversation notifications. */
export function conversationNotifications(): ConversationNotifications | undefined {
	return binding
}

/** Forgets the binding; a test that registers one ends with this. */
export function clearConversationNotifications(): void {
	binding = undefined
}

/** One row of the table: the override, plus the two things only core knows about the place. */
export interface OverrideRow extends ConversationOverride {
	/** How the conversation is named as a place: "#allgemein", "Bea Bergmann". */
	address: string
	/** Where it lives, or null when this build cannot address it. */
	href: string | null
}

/**
 * The rows the table renders. They are sorted by address, so the list does not reorder itself under
 * the reader when a message arrives somewhere; the whole model is the dependency because the rows
 * are built from three of its slices and a module's own — which is what keeps the table right while
 * another device mutes something, and what one table on a settings screen can afford.
 */
export function useConversationOverrides(): OverrideRow[] {
	const model = useModel((m) => m)
	return useMemo(() => overrideRows(model), [model])
}

/** The pure half of the hook, so a test can hand it a model. */
export function overrideRows(model: Model): OverrideRow[] {
	const source = binding
	if (source === undefined) return []
	return source
		.overrides(model)
		.map((override) => ({
			...override,
			address: conversationAddress(model, override.conv),
			href: hrefOf(model, override.conv),
		}))
		.sort((a, b) => a.address.localeCompare(b.address))
}

function hrefOf(model: Model, conv: ConversationId): string | null {
	const meta = model.conversations[conv]?.meta
	return meta === undefined ? null : conversationHref(meta)
}
